import React, {useMemo} from 'react';
import {getClassNames} from '../../../utils';
import {SORT_ORDER} from '../utils/constants';
import { TSortState, IFilteringState } from '../utils/types';
import ColumnConfig from './ColumnConfig';
import Filter from './Filter';
import SortIcon from './SortIcon';

interface IFilter {
  label?: string;
  value?: string | number;
}

interface IColumn {
  key?: string;
  title?: string;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  hidden?: boolean;
  sorter?: (...args: any[]) => any;
  sortable?: boolean;
  filters?: IFilter[];
  filterable?: boolean;
  titleType?: string;
  className?: string;
  headerClassName?: string;
  onHeaderCell?: (...args: any[]) => any;
  onFilterReset?: (...args: any[]) => any;
  lastSorter?: boolean;
}

interface ISimpleColumn {
  key?: string;
  title?: string;
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  hidden?: boolean;
  sortable?: boolean;
  className?: string;
}

interface IHeaderControls {
  selectControlsLabel?: {
    selectAll?: string;
    reset?: string;
  };
  sortByLabel?: string;
}

export interface IHeaderCellProps {
  column?: IColumn;
  columns?: ISimpleColumn[];
  sorting?: TSortState;
  filtering?: IFilteringState;
  isLast?: boolean;
  isFirst?: boolean;
  resetColumns?: () => void;
  changeColumns?: (...args: any[]) => void;
  changeSorting?: (...args: any[]) => void;
  changeFiltering?: (...args: any[]) => void;
  headerControls?: IHeaderControls;
}

const ButtonWrapper = ({ children, needWrap, onClick }: { children?: React.ReactNode; needWrap?: boolean; onClick?: () => void }) => (needWrap
    ? (
        <span role="button" className="header-column" onClick={onClick}>
            {children}
        </span>
    ) : (<>{children}</>));

function HeaderCell(props: IHeaderCellProps) {
    const {
        column,
        columns,
        sorting,
        filtering,
        resetColumns,
        changeColumns,
        changeSorting = () => {},
        changeFiltering = () => {},
        headerControls = {},
        isLast,
        isFirst,
    } = props;
    const {
        key,
        title,
        width,
        hidden,
        minWidth,
        maxWidth,
        sorter,
        sortable: sortableProp,
        filters,
        filterable,
        titleType,
        className,
        onHeaderCell,
        onFilterReset,
        headerClassName,
        lastSorter,
    } = column || {};

    if (hidden) return null;

    const sortable = sortableProp || typeof sorter === 'function';
    const hasActiveSort = sorting?.key === key;
    const sortDirection = hasActiveSort && sorting?.direction ? SORT_ORDER.indexOf(sorting?.direction) : 0;
    const filterValues = filtering?.find((item) => item.key === key) || { key, value: [] };
    const headerCellProps = typeof onHeaderCell === 'function' ? onHeaderCell(column) || {} : {};
    const { className: classNameProp, ...headerCellPops } = headerCellProps;
    const classNameTH = getClassNames(className, headerClassName, classNameProp,
        { 'column-sort': sortable },
        { 'column-filter': filterable },
        { 'column-fixed': titleType === 'columns' },
        { sorted: hasActiveSort },
        { filtered: !!filterValues?.value?.length && filterValues?.value?.length > 0 });
    const isButton = useMemo(() => (filterable || sortable), [sortable, filterable]);
    const onSort = () => {
        changeSorting(sortDirection === SORT_ORDER.length - 1
            ? {} : { key, sorter, direction: SORT_ORDER[sortDirection + 1] });
    };
    const sorterComponent = sortable && (
        <SortIcon
            isSorted={hasActiveSort}
            direction={SORT_ORDER[sortDirection] || SORT_ORDER[0]}
        />
    );
    const onFilter = (value) => {
        changeFiltering({ key, value });
    };
    const filterComponent = filterable && (
        <Filter
            filters={filters}
            selectedKeys={filterValues.value}
            headerControls={headerControls}
            setSelectedKeys={onFilter}
            onReset={onFilterReset}
        />
    );
    const onClick = () => {
        if (sortable) onSort();
    };

    if (titleType === 'columns') {
        return (
            <th
                role="columnheader"
                key={key}
                width={width}
                style={{ minWidth, maxWidth, width }}
                className={classNameTH}
                {...headerCellPops}
            >
                <ColumnConfig
                    columns={columns}
                    resetColumns={resetColumns}
                    changeColumns={changeColumns}
                    headerControls={headerControls}
                />
            </th>
        );
    }

    return (
        <th
            role="columnheader"
            key={key}
            width={width}
            style={{ minWidth, maxWidth, width }}
            className={classNameTH}
            {...headerCellPops}
        >
            {(isLast && !isFirst) && filterComponent}

            <ButtonWrapper needWrap={isButton} onClick={onClick}>
                {(!lastSorter && isLast && !isFirst && sorterComponent) }
                {title !== undefined ? title : key}
                {(lastSorter && sorterComponent) || ((!isLast || isFirst) && sorterComponent)}
            </ButtonWrapper>
            {(!isLast || isFirst) && filterComponent}
        </th>
    );
}

export default HeaderCell;
